"use client";

import { useEffect, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <header className="relative w-full min-h-screen overflow-hidden flex flex-col justify-end pb-12 md:pb-24 pt-32">
      {/* Background Image with Cinematic Animation */}
      <div className="absolute inset-0 z-0 bg-black">
        <AppImage
          src="https://images.unsplash.com/photo-1612544409025-e1f6a56c1152"
          alt="Professional film production set with cameras, lighting equipment, and crew members working on a cinematic scene"
          className="w-full h-full object-cover animate-cinematic opacity-0" />
        
        {/* Stage Curtain Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-80"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 mix-blend-overlay"></div>
      </div>

      {/* Floating "Now Playing" Card */}
      <div className="absolute top-32 right-6 md:right-12 z-20 animate-slide-up opacity-0 [animation-delay:2.5s]">
        <div className="glass-card p-5 rounded-3xl w-64 shadow-2xl">
          <div className="relative overflow-hidden">
            {/* Shimmer Effect */}
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/5 to-transparent z-0 pointer-events-none animate-shimmer-effect"></div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  Now Playing
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  <span className="text-xs font-medium text-primary">Live</span>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Icon name="FilmIcon" size={20} />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">12 Productions</p>
                  <p className="text-xs text-muted-foreground">Active Projects</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl border bg-white/5 border-white/5">
                <span className="text-xs text-muted-foreground">This Year</span>
                <span className="text-sm font-semibold text-secondary">25 Awards</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        {/* Left Column: Primary Headline (Spans 7 columns) */}
        <div className="md:col-span-7 relative">
          {/* Decorative Label */}
          <div className="flex items-center gap-3 mb-6 animate-slide-up opacity-0 [animation-delay:1.2s]">
            <span className="h-[1px] w-8 bg-primary/60"></span>
            <span className="text-xs font-mono uppercase tracking-widest text-primary">
              Established 2010
            </span>
          </div>

          <h1 className="font-display font-bold text-white leading-[0.85] tracking-tight">
            <span className="block text-[15vw] md:text-[9rem] lg:text-[11rem] animate-slide-up opacity-0 [animation-delay:1.4s] text-white drop-shadow-2xl">
              WHERE
            </span>
            <div className="flex items-baseline gap-4 md:gap-8 -mt-2 md:-mt-8 animate-slide-up opacity-0 [animation-delay:1.6s]">
              <span className="text-[15vw] md:text-[9rem] lg:text-[11rem] font-accent italic font-thin text-secondary/60">
                Stories
              </span>
            </div>
            <span className="block text-[15vw] md:text-[9rem] lg:text-[11rem] animate-slide-up opacity-0 [animation-delay:1.8s] text-white drop-shadow-2xl">
              COME ALIVE
            </span>
          </h1>
        </div>

        {/* Right Column: Description (Starts at col 9, leaving col 8 empty) */}
        <div className="md:col-span-4 md:col-start-9 flex flex-col justify-end pb-4 md:pb-8">
          <div className="relative overflow-hidden bg-card/60 backdrop-blur-2xl border border-white/10 p-8 rounded-2xl shadow-2xl animate-slide-up opacity-0 [animation-delay:2s] ring-1 ring-white/5">
            <div className="relative z-10">
              <p className="text-lg md:text-xl text-white font-light leading-relaxed mb-8 antialiased">
                Premium entertainment production services for film, television, and live events. Creating unforgettable experiences since 2010.
              </p>

              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-4 border-t border-white/20 pt-6">
                  <div>
                    <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Productions
                    </span>
                    <span className="text-2xl font-display text-white">50+</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Experience
                    </span>
                    <span className="text-2xl font-display text-white">15 Years</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    href="/services"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-red-800 px-6 py-3 rounded-full text-sm font-medium text-white transition-all duration-300 glow-primary hover:glow-primary-hover">
                    
                    <Icon name="FilmIcon" size={18} />
                    Explore Productions
                  </Link>
                  <button className="flex items-center justify-center gap-2 border border-white/30 px-6 py-3 rounded-full text-sm font-medium text-white hover:bg-white/10 transition-all">
                    <Icon name="PlayCircleIcon" size={18} />
                    View Showreel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-slide-up opacity-0 [animation-delay:2.2s]">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Scroll
        </span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-primary to-transparent"></div>
      </div>
    </header>);

}